import { useRouter } from 'next/router';
import { Fragment, ReactNode, useEffect } from 'react';
import { emit } from '@zapier/events-toolkit-browser';
import { useTrackEvent } from './useTrackEvent';
import { captureException } from '@sentry/nextjs';

// The page `/partner/iframe/login` is frequently the `document.referrer` of
// this page, but we don't actual want to record that. Instead, the
// `/partner/iframe/login` page will send a `referer` parameter in the URL
// params that we can read and send to the Avro event instead.
function getReferrer() {
  const params = new URLSearchParams(location.search);
  // Check both `referer` and `referrer` in case someone misspells it in the
  // future. Considering that the HTTP header and the JS property are spelled
  // differently, there's a lot of confusion here. Checking both is easy. Also
  // the identity code uses `from`, so let's include that too.
  return (
    params.get('from') ||
    params.get('referer') ||
    params.get('referrer') ||
    document.referrer
  );
}

type Props = {
  children: ReactNode;
};

export function TrackRouting({ children }: Props) {
  const trackEvent = useTrackEvent();
  const router = useRouter();

  useEffect(() => {
    const referer = getReferrer();
    const isRouteHydrated = !(
      router.asPath.includes('[') && router.asPath.includes(']')
    );
    // Next.js renders the page client side WITHOUT query params; let's wait
    // until the query params are there so we can get alternate referrers from
    // them.
    if (router.isReady && isRouteHydrated) {
      trackEvent('navigate', { path: router.asPath, referer });

      // emit an Avro PageViewEvent in parallel with the pre-Avro nagivate event.
      // this will allow us to begin shifting services over to exclusive use of
      // the PageViewEvent by the end of 2021 (when the necessary ETL adjustments
      // have been incorporated for attribution, etc.)

      // We only need to specify the system name that should be used for the
      // PageViewEvent The other PageViewEvent fields are set by automatically
      // by events-toolkit-browser and the v4 Events API
      // https://engineering.zapier.com/guides/event-streaming/tracking-events/
      try {
        emit(
          { system_name: 'identity', referer },
          'web_analytics.tracking.page_view.PageViewEvent'
        );
      } catch (err) {
        captureException(err, {
          tags: {
            section: 'TrackRouting',
            type: 'PageViewEvent',
          },
        });
      }
    }
  }, [router.asPath, router.isReady]);

  return <Fragment>{children}</Fragment>;
}
